const CATEGORIES = ["strengths", "weaknesses", "opportunities", "threats"];

const ATHLETIC_SECTION_LABEL = "athleticism";

const BASEBALL_DOMAIN_KEYWORDS = {
  hitting: [
    "hit", "hitting", "bat", "bat path", "swing", "barrel", "plate", "contact",
    "exit velocity", "launch angle", "approach", "timing", "load", "stride"
  ],
  pitching: [
    "pitch", "pitching", "velo", "velocity", "arm slot", "release", "bullpen", "command",
    "control", "spin", "fastball", "curveball", "slider", "changeup", "mechanics"
  ],
  fielding: [
    "field", "fielding", "glove", "infield", "outfield", "range", "footwork", "ground ball",
    "fly ball", "throwing", "transfer", "double play", "defense", "reaction"
  ]
};

const toArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_err) {
    return [];
  }
};

const entryToNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : NaN;
};

const normalizeTask = (task) => {
  if (!task) return { text: "", completed: false };
  if (typeof task === "string") return { text: task.trim(), completed: false };
  return {
    text: String(task.text || task.item || task.action || "").trim(),
    completed: Boolean(task.completed ?? task.is_completed)
  };
};

const extractActionTextAndCompletion = (action) => {
  if (!action) return [];
  if (typeof action === "string") return [{ text: action.trim(), completed: false }];

  const taskArray = Array.isArray(action.tasks) ? action.tasks.map(normalizeTask).filter((t) => t.text !== "") : [];
  if (taskArray.length > 0) return taskArray;

  const single = normalizeTask(action);
  return single.text ? [single] : [];
};

const containsKeyword = (text, keywords) => {
  const lower = String(text || "").toLowerCase();
  return keywords.some((keyword) => lower.includes(keyword));
};

const extractActionPlansFromEntry = (entry) => {
  if (!Array.isArray(entry?.actions)) return [];
  return entry.actions.map((action, idx) => {
    const microPointId = String(
      action?.microPointId ||
      action?.micro_point_id ||
      action?.development_item_micro_point_id ||
      ""
    ).trim();
    const microPointTitle = String(
      action?.microPointTitle ||
      action?.micro_point_title ||
      action?.microPoint ||
      ""
    ).trim();
    const taskSteps = Array.isArray(action?.tasks)
      ? action.tasks
        .map(normalizeTask)
        .filter((task) => task.text !== "")
      : extractActionTextAndCompletion(action);
    const firstPendingStepIndex = taskSteps.findIndex((task) => !task.completed);
    return {
      micro_point_index: idx + 1,
      micro_point_id: microPointId || null,
      micro_point_title: microPointTitle || null,
      steps: taskSteps.map((task, taskIdx) => ({
        step_index: taskIdx + 1,
        text: task.text,
        completed: Boolean(task.completed)
      })),
      first_pending_step_index: firstPendingStepIndex >= 0 ? (firstPendingStepIndex + 1) : null,
      completed: taskSteps.length > 0 ? taskSteps.every((task) => Boolean(task.completed)) : false
    };
  });
};

const isMicroPointPlanFilled = (plan) => {
  if (!plan || typeof plan !== "object") return false;
  if (String(plan.micro_point_id || "").trim() !== "") return true;
  if (String(plan.micro_point_title || "").trim() !== "") return true;
  return Array.isArray(plan.steps) && plan.steps.length > 0;
};

const isPriorityCompletedByMicroPoints = (actionPlans) => {
  const filledPlans = (Array.isArray(actionPlans) ? actionPlans : []).filter(isMicroPointPlanFilled);
  // Advancement gate: require all 3 micro-points complete before marking priority complete.
  if (filledPlans.length < 3) return false;
  return filledPlans.every((plan) => Boolean(plan.completed));
};

export const isAthleticPriorityEntry = (entry) => {
  if (!entry || typeof entry !== "object") return false;
  const sectionText = String(
    entry.section ||
    entry.development_section ||
    entry.developmentSection ||
    ""
  ).trim().toLowerCase();
  return sectionText === ATHLETIC_SECTION_LABEL;
};

const inferBaseballDomain = (text) => {
  const domains = Object.entries(BASEBALL_DOMAIN_KEYWORDS)
    .filter(([, keywords]) => containsKeyword(text, keywords))
    .map(([domain]) => domain);
  return domains;
};

const buildPriorityEntriesFromRow = (swotRow) => {
  const rows = [];

  CATEGORIES.forEach((category) => {
    const entries = toArray(swotRow?.[category]);
    entries
      .filter((entry) => Boolean(entry?.priority) && String(entry?.item || "").trim() !== "")
      .forEach((entry) => {
        const actionPairs = (Array.isArray(entry?.actions) ? entry.actions : [])
          .flatMap(extractActionTextAndCompletion);
        const completedByActions = actionPairs.length > 0 && actionPairs.every((a) => a.completed);
        const actionPlans = extractActionPlansFromEntry(entry);
        const completedByMicroPoints = isPriorityCompletedByMicroPoints(actionPlans);
        const hasMicroPointPlans = actionPlans.some(isMicroPointPlanFilled);
        const completed = hasMicroPointPlans
          ? completedByMicroPoints
          : (Boolean(entry?.completed) || completedByActions);
        const actionTexts = actionPairs.map((a) => a.text).filter(Boolean);
        rows.push({
          id: entry?.id || null,
          category,
          section: entry?.section || "General",
          item: String(entry?.item || "").trim(),
          priorityRank: Number.isFinite(entryToNumber(entry?.priorityRank)) ? entryToNumber(entry?.priorityRank) : 999,
          completed,
          action_texts: actionTexts,
          action_plans: actionPlans
        });
      });
  });

  return rows
    .sort((a, b) => a.priorityRank - b.priorityRank);
};

const buildAllEntriesFromRow = (swotRow) => {
  const rows = [];
  CATEGORIES.forEach((category) => {
    const entries = toArray(swotRow?.[category]);
    entries
      .filter((entry) => String(entry?.item || "").trim() !== "")
      .forEach((entry) => {
        const actionPairs = (Array.isArray(entry?.actions) ? entry.actions : [])
          .flatMap(extractActionTextAndCompletion);
        const completedByActions = actionPairs.length > 0 && actionPairs.every((a) => a.completed);
        const actionPlans = extractActionPlansFromEntry(entry);
        const completedByMicroPoints = isPriorityCompletedByMicroPoints(actionPlans);
        const hasMicroPointPlans = actionPlans.some(isMicroPointPlanFilled);
        const completed = hasMicroPointPlans
          ? completedByMicroPoints
          : (Boolean(entry?.completed) || completedByActions);
        rows.push({
          id: entry?.id || null,
          category,
          section: entry?.section || "General",
          item: String(entry?.item || "").trim(),
          priorityRank: Number.isFinite(entryToNumber(entry?.priorityRank)) ? entryToNumber(entry?.priorityRank) : 999,
          priority: Boolean(entry?.priority),
          completed,
          action_texts: actionPairs.map((a) => a.text).filter(Boolean),
          action_plans: actionPlans
        });
      });
  });
  return rows;
};

const buildPriorityEntriesFromPayload = (swotPayload) => {
  if (!Array.isArray(swotPayload?.season_priorities)) return [];
  return swotPayload.season_priorities
    .filter((entry) => String(entry?.item || "").trim() !== "")
    .map((entry) => {
      const actionPlans = Array.isArray(entry?.action_plans)
        ? entry.action_plans
        : extractActionPlansFromEntry(entry);
      const completedByMicroPoints = isPriorityCompletedByMicroPoints(actionPlans);
      const hasMicroPointPlans = actionPlans.some(isMicroPointPlanFilled);
      return {
        id: entry?.id || null,
        category: entry?.category || "unknown",
        section: entry?.section || "General",
        item: String(entry?.item || "").trim(),
        priorityRank: Number.isFinite(entryToNumber(entry?.priorityRank)) ? entryToNumber(entry?.priorityRank) : 999,
        completed: hasMicroPointPlans ? completedByMicroPoints : Boolean(entry?.completed),
        action_texts: (Array.isArray(entry?.actions) ? entry.actions : [])
          .flatMap(extractActionTextAndCompletion)
          .map((a) => a.text)
          .filter(Boolean),
        action_plans: actionPlans
      };
    })
    .sort((a, b) => a.priorityRank - b.priorityRank);
};

export const buildSwotPriorityPayload = (swotLike, options = {}) => {
  if (!swotLike) return null;

  const sport = String(options.sport || swotLike?.sport || "").toLowerCase();
  const baseballFocusDomain = String(options.baseballFocusDomain || "all").toLowerCase();
  const allEntries = Array.isArray(swotLike?.season_priorities)
    ? []
    : buildAllEntriesFromRow(swotLike);
  const sourcePriorities = Array.isArray(swotLike?.season_priorities)
    ? buildPriorityEntriesFromPayload(swotLike)
    : buildPriorityEntriesFromRow(swotLike);

  const priorities = sourcePriorities.map((entry) => {
    const combinedText = `${entry.section} ${entry.item} ${(entry.action_texts || []).join(" ")}`;
    const isAthletic = isAthleticPriorityEntry(entry);
    const baseballDomains = sport === "baseball" ? inferBaseballDomain(combinedText) : [];
    return {
      ...entry,
      is_athletic: isAthletic,
      baseball_domains: baseballDomains
    };
  });

  const classifiedAllEntries = allEntries.map((entry) => {
    const combinedText = `${entry.section} ${entry.item} ${(entry.action_texts || []).join(" ")}`;
    const isAthletic = isAthleticPriorityEntry(entry);
    const baseballDomains = sport === "baseball" ? inferBaseballDomain(combinedText) : [];
    return {
      ...entry,
      is_athletic: isAthletic,
      baseball_domains: baseballDomains
    };
  });

  const activePriorities = priorities.filter((entry) => !entry.completed);
  const skillPrioritiesUncapped = sport === "baseball"
    ? (
      baseballFocusDomain !== "all"
        ? activePriorities.filter(
          (entry) => !entry.is_athletic && entry.baseball_domains.includes(baseballFocusDomain)
        )
        : activePriorities.filter((entry) => !entry.is_athletic)
    )
    : activePriorities.filter((entry) => !entry.is_athletic);
  const athleticPrioritiesUncapped = activePriorities.filter((entry) => entry.is_athletic);
  const skillPriorities = skillPrioritiesUncapped.slice(0, 3);
  const athleticPriorities = athleticPrioritiesUncapped.slice(0, 3);
  const completedPriorities = priorities.filter((entry) => entry.completed);
  const completedSkillPriorities = completedPriorities.filter((entry) => !entry.is_athletic);
  const primarySkillPriority = skillPriorities[0] || null;
  const primaryPriorityActionPlans = Array.isArray(primarySkillPriority?.action_plans)
    ? primarySkillPriority.action_plans
    : [];
  const nextPendingMicroPoint = primaryPriorityActionPlans.find((plan) => !plan?.completed) || null;
  const nextPendingMicroPointStep = nextPendingMicroPoint
    ? (
      Number.isFinite(entryToNumber(nextPendingMicroPoint.first_pending_step_index))
        ? entryToNumber(nextPendingMicroPoint.first_pending_step_index)
        : 1
    )
    : null;

  const activeSkillEntries = classifiedAllEntries.filter((entry) => !entry.completed && !entry.is_athletic);
  const activeAthleticEntries = classifiedAllEntries.filter((entry) => !entry.completed && entry.is_athletic);
  const byCategory = (list) => ({
    strengths: list.filter((entry) => entry.category === "strengths"),
    weaknesses: list.filter((entry) => entry.category === "weaknesses"),
    opportunities: list.filter((entry) => entry.category === "opportunities"),
    threats: list.filter((entry) => entry.category === "threats"),
  });

  const firstPendingByCategory = {};
  skillPriorities.forEach((entry) => {
    if (!firstPendingByCategory[entry.category]) {
      firstPendingByCategory[entry.category] = {
        item: entry.item,
        action: entry.action_texts?.[0] || null
      };
    }
  });

  return {
    id: swotLike?.id || null,
    program_id: swotLike?.program_id || null,
    sport,
    priorities,
    skill_priorities: skillPriorities,
    athletic_priorities: athleticPriorities,
    skill_categories: byCategory(activeSkillEntries),
    athletic_categories: byCategory(activeAthleticEntries),
    season_priority_focus_items: skillPriorities.map((entry) => entry.item).filter(Boolean),
    athletic_priority_focus_items: athleticPriorities.map((entry) => entry.item).filter(Boolean),
    completed_priority_items: completedPriorities,
    completed_skill_priority_items: completedSkillPriorities,
    primary_skill_priority: primarySkillPriority,
    primary_priority_action_plans: primaryPriorityActionPlans,
    next_pending_micro_point: nextPendingMicroPoint,
    next_pending_micro_point_step: nextPendingMicroPointStep,
    first_pending_by_category: firstPendingByCategory,
    baseball_focus_domain: baseballFocusDomain
  };
};
